import { useFlag } from '@unleash/proxy-client-react'
import classnames from 'classnames'
import {
  CuiMealCardLoading,
  CuiMealFavorite,
  CuiMenuImage,
  CuiTag
} from 'front-lib'
import { useDispatch, useSelector } from 'react-redux'
import { generateChefImageUrl } from '../../../components/ChefCardRebranding/utils'
import {
  FF_MENU_RECIPES,
  FF_NEWNESS_IN_MENU,
  FF_NUTRITION_INFO,
  FF_USE_BOM_MULTIPLE_PRICES
} from '../../../constants/feature-flags'
import { getCategoryId } from '../../../redux-api-bridge/filters/selectors'
import {
  trackMealInBundleDetailClick,
  trackNutritionalInfoCardClicked
} from '../../../utils/tracking'
import { AddButton } from '../components/AddButton/AddButton'
import { CardTitle } from '../components/CardTitle/CardTitle'
import { CharacteristicsL1 } from '../components/CharacteristicsL1/CharacteristicsL1'
import { CharacteristicsL2 } from '../components/CharacteristicsL2/CharacteristicsL2'
import { ChefAvatar } from '../components/ChefAvatar/ChefAvatar'
import { UserRatingCharacteristic } from '../components/UserRatingCharacteristic/UserRatingCharacteristic'
import {
  getCharacteristicsL1,
  getCharacteristicsL2,
  getMealCardSubtitle,
  getRatingCharacteristic,
  getUserRatingCharacteristic
} from '../utils'
import {
  getAddButtonText,
  getCondensedPriceText,
  getMainTags
} from '../utils_typed'

import { useBoxOrderingModel } from 'src/hooks/useBoxOrderingModel'
import { useMealFavorite } from 'src/hooks/useMealFavorite'
import { useMembershipMainTagsText } from 'src/hooks/useMembershipMainTagsText'
import { mapMenuPromotion } from 'src/hooks/useMenuPromotion'
import { getIsFlexUserPlan } from 'src/modules/MyAccount/components/Settings/selectors'
import { getMealNutritionInfo } from 'src/redux-api-bridge/mealDetail/effects'
import {
  mealNutritionInfo,
  mealNutritionInfoButtonVisibility
} from 'src/redux-api-bridge/mealDetail/selectors'
import { getStrikethroughPrice } from 'src/utils/menu'
import { optimizeImage } from '../../../utils/utils'
import './CondensedMealCard.scss'

import classNames from 'classnames'
import { ExclusiveSkuCTA } from 'src/componentsV2/MenuMealCard/components/ExclusiveSkuCTA/ExclusiveSkuCTA'
import { MENU_ENTITY_TYPE } from 'src/constants/menu'
import { useExclusiveSKUMeal } from 'src/hooks/useExclusiveSKUMeal'
import { useIsUnityPassActive } from 'src/hooks/useIsUnityPassActive'
import { useMembershipLimit } from 'src/hooks/useMembershipLimit'
import { useUserRatingReviews } from 'src/hooks/useUserRatingReviews'
import { openNutritionModal } from 'src/redux-api-bridge/mealDetail/actions'
import { PRODUCT_ADD_SOURCES } from 'src/utils/tracking'
import { ReactComponent as NutritionIcon } from '../../../assets/svg/mealcard/bar-chart.svg'

const CondensedMealCard = ({
  meal,
  quantity,
  onAddItem,
  onRemoveItem,
  planIsCompleted,
  isExtra,
  onLikeMeal,
  onUnLikeMeal,
  isLikeMarked,
  onMealClick,
  isLoading,
  isEditable,
  index = -1,
  showSeeDetails = false,
  noExtraMealPlanIsCompleted = false,
  onChefClick = () => {}
}) => {
  const { isLiked, toggleFavorite } = useMealFavorite({
    mealId: meal.magento_id || meal.id,
    isLikeMarked,
    onLike: onLikeMeal,
    onUnlike: onUnLikeMeal
  })
  const isUnityPassActive = useIsUnityPassActive()
  const categoryId = useSelector(getCategoryId)
  const isFlexUser = useSelector(getIsFlexUserPlan)
  const nutritionInfoButtonVisibility = useSelector(state =>
    mealNutritionInfoButtonVisibility(state)
  )
  const useBOMMultiplePrices = useFlag(FF_USE_BOM_MULTIPLE_PRICES)
  const isBackInTheMenuEnabled = useFlag(FF_NEWNESS_IN_MENU)
  const recipesEnabled = useFlag(FF_MENU_RECIPES)
  const useNutritionInfo =
    useFlag(FF_NUTRITION_INFO) && nutritionInfoButtonVisibility
  const { showUserReviews } = useUserRatingReviews(
    !!meal && !!getUserRatingCharacteristic(meal)
  )

  const { membershipDiscountCopy } = useMembershipMainTagsText()

  const dispatch = useDispatch()
  const nutritionInfo = useSelector(state =>
    mealNutritionInfo(state, meal.inventoryId)
  )

  const { showPremiumFee } = useMembershipLimit()

  const {
    isValidExclusiveSKU,
    isAllowedToAddToCart,
    showExclusiveSkuCTA,
    mainTagsExclusiveSKUData
  } = useExclusiveSKUMeal({
    mealId: meal.id,
    mealName: meal.name,
    isExclusiveMembershipProduct: meal.isExclusiveMembershipProduct
  })

  const { currentBox } = useBoxOrderingModel()

  const chefImage =
    meal?.chef?.profileImageUrl ||
    generateChefImageUrl({
      firstName: meal.chef_firstname,
      lastName: meal.chef_lastname,
      isCloseUp: true,
      isMobile: false
    })

  const mealPromotion =
    meal.promotions?.length > 0 ? mapMenuPromotion(meal.promotions[0]) : null

  const strikethroughPrice = getStrikethroughPrice({
    meal,
    menuPromotion: mealPromotion,
    planIsCompleted,
    isBundle: false
  })

  const isWine = meal?.entityType === MENU_ENTITY_TYPE.WINE
  const outOfStock = isEditable && meal.stock <= 0
  const showActions = isEditable && !outOfStock && isAllowedToAddToCart

  const noExtraMealPlanStatus =
    !noExtraMealPlanIsCompleted || (noExtraMealPlanIsCompleted && quantity > 0)

  const noExtraMealPlanStatusWithExtra =
    !noExtraMealPlanIsCompleted || (noExtraMealPlanIsCompleted && isExtra)

  const onFavoriteClick = e => {
    e.stopPropagation()
    toggleFavorite()
  }

  if (isLoading) {
    return <CuiMealCardLoading />
  }

  const onSeeDetailsClick = e => {
    e.stopPropagation?.()
    e.preventDefault?.()

    trackMealInBundleDetailClick()
    onMealClick()
  }

  const _onAddItem = () => {
    onAddItem(meal, PRODUCT_ADD_SOURCES.MENU)
  }

  const _onRemoveItem = () => {
    onRemoveItem(meal)
  }

  const handleMealClick = e => {
    e.stopPropagation?.()
    e.preventDefault?.()

    if (showExclusiveSkuCTA) {
      return
    }

    onMealClick(e)
  }

  const handleChefClick = e => {
    e.stopPropagation?.()
    e.preventDefault?.()
    if (onChefClick) {
      onChefClick()
    }
  }

  const priceText = getAddButtonText({
    meal,
    planIsCompleted,
    isExtra,
    isFlexUser,
    noExtraMealPlanIsCompleted,
    currentBOMBox: currentBox,
    showPremiumFee,
    useBOMMultiplePrices,
    isUnityPassActive
  })

  const onNutritionClick = async e => {
    e.stopPropagation?.()
    e.preventDefault?.()

    const footerData = {
      showActions,
      isExtra,
      noExtraMealPlanStatus,
      priceText,
      showPremiumFee,
      strikethroughPrice,
      isEditable,
      reachedPlanSize: noExtraMealPlanIsCompleted && !isExtra,
      index: 1
    }
    dispatch(
      openNutritionModal({
        mealId: meal.inventoryId,
        footerData
      })
    )
    if (!nutritionInfo) {
      dispatch(getMealNutritionInfo(meal.inventoryId))
    }
    trackNutritionalInfoCardClicked()
  }

  const characteristics_l1 = getCharacteristicsL1(meal, 3)
  const characteristics_l2 = getCharacteristicsL2(
    meal,
    isWine ? 3 : 2,
    categoryId,
    false
  )
  const ratingCharacteristic = getRatingCharacteristic(meal)
  const userRatingCharacteristic = showUserReviews
    ? getUserRatingCharacteristic(meal)
    : null
  const hasUserReview = !!userRatingCharacteristic

  const mainTags = getMainTags({
    meal,
    max: 3,
    backInTheMenuEnabled: isBackInTheMenuEnabled,
    recipesEnabled,
    menuPromotion: !!mealPromotion && mealPromotion,
    exclusiveSKUData: mainTagsExclusiveSKUData,
    isUnityPassActive,
    membershipDiscountCopy
  })

  const condensedPriceText = getCondensedPriceText({
    meal,
    planIsCompleted,
    isExtra,
    isFlexUser,
    noExtraMealPlanIsCompleted,
    currentBOMBox: currentBox,
    useBOMMultiplePrices,
    showPremiumFee,
    isUnityPassActive
  })

  return (
    <div
      className={classnames('card-condensed', {
        'card-condensed--sold-out': outOfStock,
        'card-v2__exclusive-sku': isValidExclusiveSKU
      })}
      onClick={handleMealClick}>
      <CuiMenuImage
        primary_image={optimizeImage({
          url:
            meal.primaryImageUrl || meal.imageUrl || meal.full_path_meal_image,
          width: 500
        })}
        className="card-condensed__img"
      />
      <div className="card-condensed__details">
        <div className="card-condensed__row card-condensed__row--start">
          <div className="card-condensed__column">
            <div className="card-condensed__tags-favorites-wrapper">
              <div className="card-condensed__tags-wrapper">
                {mainTags.map(tag => (
                  <CuiTag
                    key={tag.label}
                    color={tag.color}
                    fill="solid"
                    size="small"
                    weight={tag.weight}
                    className={classnames('card-condensed__tag', {
                      'card-condensed__tag-with-icon': !!tag.icon
                    })}>
                    {tag.icon}
                    <span dangerouslySetInnerHTML={{ __html: tag.label }} />
                  </CuiTag>
                ))}
              </div>
              {onLikeMeal && onUnLikeMeal && (
                <CuiMealFavorite
                  checked={isLiked}
                  onClick={onFavoriteClick}
                  className={classnames('card-condensed__favorite', {
                    'card-condensed__favorite--empty': !isLiked
                  })}
                />
              )}
            </div>

            {useNutritionInfo && !isWine && (
              <button
                id={index ? `meal-card-${index}-nutrition` : undefined}
                className="card-condensed__nutrition-button"
                onClick={onNutritionClick}>
                <NutritionIcon />
              </button>
            )}

            <div
              className={classNames('card-condensed__details-title', {
                'card-condensed__details-title--with-nutrition': useNutritionInfo
              })}>
              <CardTitle
                title={meal.name}
                subtitle={getMealCardSubtitle(meal)}
                onClick={handleMealClick}
                size="small"
              />
              <ChefAvatar
                image={optimizeImage({ url: chefImage, width: 100 })}
                fallbackImage={meal.full_path_chef_image}
                onClick={handleChefClick}
                name={`by ${meal.chef_firstname || ''} ${meal.chef_lastname ||
                  ''}`}
                className="card-condensed__chef"
                size="small"
              />
            </div>
          </div>
        </div>
        {!showExclusiveSkuCTA && (
          <div className="card-condensed__characteristics">
            {hasUserReview && (
              <UserRatingCharacteristic
                reviewDetails={userRatingCharacteristic.reviewDetails}
                customerRating={userRatingCharacteristic.customerRating}
              />
            )}
            {!hasUserReview && !!ratingCharacteristic && (
              <CharacteristicsL2
                list={[ratingCharacteristic]}
                classNames={'card-condensed__characteristics__rating'}
              />
            )}
            {characteristics_l1.length > 0 && !isWine && (
              <CharacteristicsL1
                list={characteristics_l1}
                mealId={meal.id}
                size="small"
              />
            )}
            {characteristics_l2.length > 0 && (
              <CharacteristicsL2 list={characteristics_l2} size="small" />
            )}
          </div>
        )}
        {showActions && (isExtra || noExtraMealPlanStatus) && (
          <div
            onClick={e => e.stopPropagation()}
            className="card-condensed__row card-condensed__row--end">
            <div
              className={classnames('card-condensed__price', {
                '-has-fee': !!condensedPriceText
              })}>
              <span className="card-condensed-details-price-text">
                {condensedPriceText}
              </span>
              {!!strikethroughPrice && (
                <span className="strikethrough-price-text">
                  {strikethroughPrice}
                </span>
              )}
            </div>
            <AddButton
              quantity={quantity}
              add={_onAddItem}
              remove={_onRemoveItem}
              addEnabled={
                quantity < meal.stock && noExtraMealPlanStatusWithExtra
              }
            />
          </div>
        )}
        {showExclusiveSkuCTA && <ExclusiveSkuCTA meal={meal} />}
        {outOfStock && <div className="card-condensed__sold-out">SOLD OUT</div>}
        {showSeeDetails && (
          <div
            role="button"
            className="see-details"
            onClick={onSeeDetailsClick}>
            See details
          </div>
        )}
      </div>
    </div>
  )
}

export { CondensedMealCard }
